import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { affiliatesAPI } from '../../lib/api';
import { t } from '../../lib/i18n';
import { btnDanger, btnGhost, btnPrimary, EmptyState, Field, inputCls, Modal, panelCls, StatusBadge } from '../../components/ui';
import { nyTime } from '../../lib/nyTime';

function ResolveModal({ item, action, onClose, onDone }) {
  const [note, setNote] = useState('');
  const [reference, setReference] = useState('');
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      await affiliatesAPI.resolvePayout(item.id, { action, note, reference });
      toast.success(t('Done'));
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('Action failed'));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal title={`${t(action)} — ${item.amount} (${item.member?.name || ''})`} onClose={onClose}>
      <div className="space-y-4">
        <p className="text-sm text-mms-muted">
          {t('Method')}: {item.method?.type} · {item.method?.details}
        </p>
        {action === 'pay' && (
          <Field label={t('Payment reference (optional)')}>
            <input className={inputCls} value={reference} onChange={(e) => setReference(e.target.value)} autoFocus />
          </Field>
        )}
        <Field label={t('Note (optional)')}>
          <input className={inputCls} value={note} onChange={(e) => setNote(e.target.value)} />
        </Field>
      </div>
      <div className="mt-5 flex justify-end gap-2">
        <button onClick={onClose} className={btnGhost}>{t('Cancel')}</button>
        <button onClick={run} disabled={busy} className={action === 'reject' ? btnDanger : btnPrimary}>
          {busy ? t('Working…') : t(action)}
        </button>
      </div>
    </Modal>
  );
}

export default function PayoutsPage() {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState('pending');
  const [acting, setActing] = useState(null);

  const load = useCallback(() => {
    affiliatesAPI.payouts({ status })
      .then(({ data }) => setData(data))
      .catch(() => toast.error(t('Could not load payouts')));
  }, [status]);
  useEffect(load, [load]);

  return (
    <div>
      <h1 className="mb-4 text-lg font-semibold">{t('Payouts')}</h1>
      <div className="mb-3">
        <select className={`${inputCls} !w-40`} value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="pending">{t('pending')}</option>
          <option value="paid">{t('paid')}</option>
          <option value="rejected">{t('rejected')}</option>
          <option value="">{t('All statuses')}</option>
        </select>
      </div>
      {data === null ? (
        <p className="text-sm text-mms-muted">{t('Loading…')}</p>
      ) : data.items.length === 0 ? (
        <EmptyState>{t('No payout requests here.')}</EmptyState>
      ) : (
        <div className={`${panelCls} overflow-x-auto`}>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-mms-border text-left text-xs text-mms-faint">
                <th className="px-4 py-2.5 font-medium">{t('When')}</th>
                <th className="px-4 py-2.5 font-medium">{t('Member')}</th>
                <th className="px-4 py-2.5 font-medium text-right">{t('Amount')}</th>
                <th className="px-4 py-2.5 font-medium text-right">{t('Available')}</th>
                <th className="px-4 py-2.5 font-medium">{t('Method')}</th>
                <th className="px-4 py-2.5 font-medium">{t('Status')}</th>
                <th className="px-4 py-2.5" />
              </tr>
            </thead>
            <tbody>
              {data.items.map((p) => (
                <tr key={p.id} className="border-b border-mms-border/60 last:border-0">
                  <td className="px-4 py-2.5 whitespace-nowrap text-xs text-mms-muted">
                    {nyTime(p.requested_at)}
                  </td>
                  <td className="px-4 py-2.5">{p.member?.name || '—'}</td>
                  <td className="px-4 py-2.5 text-right mms-num">{p.amount}</td>
                  <td className="px-4 py-2.5 text-right mms-num text-mms-muted">{p.balance?.available}</td>
                  <td className="px-4 py-2.5 text-xs text-mms-muted">{p.method?.type}</td>
                  <td className="px-4 py-2.5"><StatusBadge status={p.status} /></td>
                  <td className="px-4 py-2.5 text-right whitespace-nowrap">
                    {p.status === 'pending' && (
                      <>
                        <button onClick={() => setActing({ item: p, action: 'pay' })} className={`${btnGhost} mr-1.5`}>{t('pay')}</button>
                        <button onClick={() => setActing({ item: p, action: 'reject' })} className={btnDanger}>{t('reject')}</button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {acting && (
        <ResolveModal item={acting.item} action={acting.action}
          onClose={() => setActing(null)}
          onDone={() => { setActing(null); load(); }} />
      )}
    </div>
  );
}
